import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Alert,
    AlertDescription,
} from '../components/ui/alert';
import FloorPlan, { TABLE_STATUS_COLORS, TABLE_STATUS_LABELS } from '../components/FloorPlan';
import { 
    Calendar,
    Clock,
    Users,
    MapPin,
    Plus,
    Edit2,
    Trash2,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    Phone,
    Mail,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    Search,
    Filter,
    UserCheck,
    UserPlus,
    Utensils,
    Eye,
    Ban,
    Sparkles,
    ClipboardList,
    ArrowRight,
    Bell,
    TrendingUp,
    Home,
    Coffee,
    Sun,
    Crown,
    Wine,
    CalendarCheck
} from 'lucide-react';
import MobilePageHeader from '../components/MobilePageHeader';

// Statuts de réservation
const RESERVATION_STATUS = {
    brouillon: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: Edit2 },
    confirmee: { label: 'Confirmée', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
    arrivee: { label: 'Arrivée', color: 'bg-amber-100 text-amber-700', icon: UserCheck },
    installee: { label: 'Installée', color: 'bg-green-100 text-green-700', icon: Utensils },
    annulee: { label: 'Annulée', color: 'bg-red-100 text-red-700', icon: XCircle },
    no_show: { label: 'No-show', color: 'bg-red-100 text-red-700', icon: Ban },
    terminee: { label: 'Terminée', color: 'bg-gray-100 text-gray-600', icon: CheckCircle2 }
};

const ZONE_CONFIG = {
    terrasse: { label: 'Terrasse', color: 'bg-amber-100 text-amber-700', icon: Sun },
    salle: { label: 'Salle', color: 'bg-blue-100 text-blue-700', icon: Home },
    vip: { label: 'VIP', color: 'bg-purple-100 text-purple-700', icon: Crown },
    bar: { label: 'Bar', color: 'bg-pink-100 text-pink-700', icon: Wine }
};

// Composant KPI Card
const KPICard = ({ title, value, subtitle, icon: Icon, color = 'text-primary', trend }) => (
    <Card className="relative overflow-hidden">
        <CardContent className="p-4">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm text-muted-foreground">{title}</p>
                    <p className={`text-2xl font-bold ${color}`}>{value}</p>
                    {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                </div>
                <div className={`p-2 rounded-lg ${color.replace('text-', 'bg-').replace('-600', '-100').replace('-700', '-100')}`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                </div>
            </div>
            {trend && (
                <div className="absolute bottom-2 right-2">
                    <TrendingUp className={`w-4 h-4 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`} />
                </div>
            )}
        </CardContent>
    </Card>
);

// Composant Alerte
const AlertItem = ({ alert, onAction }) => {
    const severityColors = {
        error: 'border-red-400 bg-red-50',
        warning: 'border-amber-400 bg-amber-50',
        info: 'border-blue-400 bg-blue-50'
    };
    
    const severityIcons = {
        error: AlertTriangle,
        warning: Clock,
        info: Bell
    };
    
    const Icon = severityIcons[alert.severity] || Bell;
    
    return (
        <div className={`flex items-center gap-3 p-3 rounded-lg border-l-4 ${severityColors[alert.severity] || severityColors.info}`}>
            <Icon className={`w-4 h-4 flex-shrink-0 ${alert.severity === 'error' ? 'text-red-600' : alert.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'}`} />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{alert.message}</p>
                <p className="text-xs text-muted-foreground">{alert.type}</p>
            </div>
            {alert.reservation_id && (
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 px-2"
                    onClick={() => onAction?.(alert)}
                >
                    <Eye className="w-3 h-3" />
                </Button>
            )}
        </div>
    );
};

// Composant Carte de Réservation
const ReservationCard = ({ reservation, onAction, compact = false }) => {
    const status = RESERVATION_STATUS[reservation.status] || RESERVATION_STATUS.confirmee;
    const StatusIcon = status.icon;
    
    if (compact) {
        return (
            <div 
                className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onAction?.('view', reservation)}
            >
                <div className={`p-1.5 rounded ${status.color}`}>
                    <StatusIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{reservation.customer_name}</div>
                    <div className="text-xs text-muted-foreground">
                        {reservation.time} • {reservation.party_size} pers • Table {reservation.table_number || '?'}
                    </div>
                </div>
                <Badge variant="outline" className={status.color}>
                    {status.label}
                </Badge>
            </div>
        );
    }
    
    return (
        <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h4 className="font-semibold">{reservation.customer_name}</h4>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Clock className="w-3 h-3" />
                            {reservation.time}
                            <span className="mx-1">•</span>
                            <Users className="w-3 h-3" />
                            {reservation.party_size} pers
                        </div>
                    </div>
                    <Badge className={status.color}>{status.label}</Badge>
                </div>
                
                <div className="flex items-center gap-2 text-sm mb-3">
                    <MapPin className="w-3 h-3 text-muted-foreground" />
                    <span>
                        Table {reservation.table_number || 'Non assignée'}
                        {reservation.table_zone && (
                            <span className="text-muted-foreground"> ({reservation.table_zone})</span>
                        )}
                    </span>
                </div>
                
                {reservation.customer_phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Phone className="w-3 h-3" />
                        {reservation.customer_phone}
                    </div>
                )}
                
                {reservation.notes && (
                    <p className="text-sm text-muted-foreground bg-slate-50 dark:bg-slate-900 p-2 rounded mb-3">
                        {reservation.notes}
                    </p>
                )}
                
                <div className="flex gap-2 flex-wrap">
                    {reservation.status === 'confirmee' && (
                        <>
                            <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => onAction?.('arrive', reservation)}
                            >
                                <UserCheck className="w-3 h-3 mr-1" />
                                Arrivée
                            </Button>
                            <Button 
                                size="sm"
                                onClick={() => onAction?.('seat', reservation)}
                            >
                                <UserPlus className="w-3 h-3 mr-1" />
                                Installer
                            </Button>
                        </>
                    )}
                    
                    {reservation.status === 'arrivee' && (
                        <Button 
                            size="sm"
                            onClick={() => onAction?.('seat', reservation)}
                        >
                            <UserPlus className="w-3 h-3 mr-1" />
                            Installer
                        </Button>
                    )}
                    
                    {['confirmee', 'arrivee'].includes(reservation.status) && (
                        <>
                            <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => onAction?.('no-show', reservation)}
                            >
                                <Ban className="w-3 h-3 mr-1" />
                                No-show
                            </Button>
                            <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => onAction?.('cancel', reservation)}
                            >
                                <XCircle className="w-3 h-3 mr-1" />
                                Annuler
                            </Button>
                        </>
                    )}
                    
                    <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => onAction?.('edit', reservation)}
                    >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Modifier
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

// Page Principale
const ReservationsPage = () => {
    const { api, user, hasPermission } = useAuth();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    
    // Data
    const [todayData, setTodayData] = useState(null);
    const [tables, setTables] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [availableTables, setAvailableTables] = useState([]);
    const [zones, setZones] = useState([]);
    
    // UI State
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [activeTab, setActiveTab] = useState('floor');
    const [selectedTable, setSelectedTable] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [zoneFilter, setZoneFilter] = useState('all');
    
    // Dialogs
    const [reservationDialog, setReservationDialog] = useState(false);
    const [tableDialog, setTableDialog] = useState(false);
    const [seatDialog, setSeatDialog] = useState(false);
    const [editingReservation, setEditingReservation] = useState(null);
    const [editingTable, setEditingTable] = useState(null);
    const [seatReservation, setSeatReservation] = useState(null);
    
    // Permissions
    const canManage = hasPermission?.('manage_reservations') || 
        ['DIRECTEUR', 'DIRECTEUR_ADJOINT', 'ADMIN', 'ACCUEIL', 'CAISSE'].includes(user?.role);
    const canDelete = ['DIRECTEUR', 'ADMIN'].includes(user?.role);
    // Mode lecture seule pour les serveurs
    const isReadOnlyMode = ['SERVEUR', 'SERVEUSE'].includes(user?.role);
    
    // Form states
    const [resForm, setResForm] = useState({
        table_id: '',
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        date: selectedDate,
        time: '19:00',
        duration_minutes: 90,
        party_size: 2,
        zone_preference: '',
        notes: '',
        special_requests: '',
        status: 'confirmee'
    });
    
    // Fetch data
    const fetchData = useCallback(async () => {
        try {
            setRefreshing(true);
            
            const [todayRes, tablesRes, reservationsRes, zonesRes] = await Promise.all([
                api().get('/reservations/today'),
                api().get(`/reservations/tables?date=${selectedDate}`),
                api().get(`/reservations?date=${selectedDate}`),
                api().get('/reservations/zones/list')
            ]);
            
            setTodayData(todayRes.data);
            setTables(tablesRes.data || []);
            setReservations(reservationsRes.data || []);
            setZones(zonesRes.data?.zones || []);
            
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [api, selectedDate]);
    
    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000); // Refresh every 30s
        return () => clearInterval(interval);
    }, [fetchData]);
    
    // Update form date when selectedDate changes
    useEffect(() => {
        setResForm(prev => ({ ...prev, date: selectedDate }));
    }, [selectedDate]);
    
    // Fetch available tables when form changes
    const fetchAvailableTables = useCallback(async () => {
        if (!resForm.date || !resForm.time || !resForm.party_size) return;
        
        try {
            const response = await api().post('/reservations/available-tables', {
                date: resForm.date,
                time: resForm.time,
                party_size: resForm.party_size,
                duration_minutes: resForm.duration_minutes,
                zone: resForm.zone_preference || null
            });
            setAvailableTables(response.data?.available || []);
        } catch (error) {
            console.error('Error fetching available tables:', error);
        }
    }, [api, resForm.date, resForm.time, resForm.party_size, resForm.duration_minutes, resForm.zone_preference]);
    
    useEffect(() => {
        if (reservationDialog) {
            fetchAvailableTables();
        }
    }, [reservationDialog, fetchAvailableTables]);
    
    // Handlers
    const handleTableAction = async (action, table) => {
        try {
            switch (action) {
                case 'reserve':
                    setResForm(prev => ({ 
                        ...prev, 
                        table_id: table.id,
                        zone_preference: table.zone
                    }));
                    setReservationDialog(true);
                    break;
                    
                case 'arrive':
                    if (table.current_reservation) {
                        await api().post(`/reservations/${table.current_reservation.id}/arrive`);
                        toast.success('Client marqué comme arrivé');
                        fetchData();
                    }
                    break;
                    
                case 'seat':
                    if (table.current_reservation || table.next_reservation) {
                        setSeatReservation(table.current_reservation || table.next_reservation);
                        setSeatDialog(true);
                    }
                    break;
                    
                case 'clear':
                    await api().post(`/tables/${table.id}/clear`);
                    toast.success('Table marquée comme débarrassée');
                    fetchData();
                    break;
                    
                case 'ready':
                    await api().post(`/tables/${table.id}/ready`);
                    toast.success('Table prête');
                    fetchData();
                    break;
                    
                case 'out-of-service':
                    await api().post(`/tables/${table.id}/out-of-service`);
                    toast.success('Table mise hors service');
                    fetchData();
                    break;
                    
                case 'edit':
                    setEditingTable(table);
                    setTableDialog(true);
                    break;
                    
                case 'view-order':
                    // Navigate to POS with this table's order
                    if (table.current_order_id) {
                        window.location.href = `/pos?order=${table.current_order_id}`;
                    }
                    break;
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const handleReservationAction = async (action, reservation) => {
        try {
            switch (action) {
                case 'view':
                    setEditingReservation(reservation);
                    setReservationDialog(true);
                    break;
                    
                case 'edit':
                    setEditingReservation(reservation);
                    setResForm({
                        table_id: reservation.table_id || '',
                        customer_name: reservation.customer_name || '',
                        customer_phone: reservation.customer_phone || '',
                        customer_email: reservation.customer_email || '',
                        date: reservation.date || selectedDate,
                        time: reservation.time || '19:00',
                        duration_minutes: reservation.duration_minutes || 90,
                        party_size: reservation.party_size || 2,
                        zone_preference: reservation.zone_preference || '',
                        notes: reservation.notes || '',
                        special_requests: reservation.special_requests || '',
                        status: reservation.status || 'confirmee'
                    });
                    setReservationDialog(true);
                    break;
                    
                case 'arrive':
                    await api().post(`/reservations/${reservation.id}/arrive`);
                    toast.success('Client marqué comme arrivé');
                    fetchData();
                    break;
                    
                case 'seat':
                    setSeatReservation(reservation);
                    setSeatDialog(true);
                    break;
                    
                case 'no-show':
                    await api().post(`/reservations/${reservation.id}/no-show`);
                    toast.success('Marqué comme no-show');
                    fetchData();
                    break;
                    
                case 'cancel':
                    if (confirm('Annuler cette réservation ?')) {
                        await api().post(`/reservations/${reservation.id}/cancel`);
                        toast.success('Réservation annulée');
                        fetchData();
                    }
                    break;
                    
                case 'delete':
                    if (confirm('Supprimer définitivement cette réservation ?')) {
                        await api().delete(`/reservations/${reservation.id}`);
                        toast.success('Réservation supprimée');
                        fetchData();
                    }
                    break;
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const handleSaveReservation = async () => {
        if (!resForm.customer_name) {
            toast.error('Nom du client requis');
            return;
        }
        
        try {
            if (editingReservation) {
                await api().put(`/reservations/${editingReservation.id}`, resForm);
                toast.success('Réservation modifiée');
            } else {
                const autoAssign = !resForm.table_id;
                await api().post(`/reservations?auto_assign=${autoAssign}`, resForm);
                toast.success('Réservation créée');
            }
            
            setReservationDialog(false);
            setEditingReservation(null);
            resetResForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const handleSeatCustomer = async () => {
        if (!seatReservation) return;
        
        try {
            await api().post(`/reservations/${seatReservation.id}/seat`, {});
            toast.success('Client installé - Commande créée');
            setSeatDialog(false);
            setSeatReservation(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const resetResForm = () => {
        setResForm({
            table_id: '',
            customer_name: '',
            customer_phone: '',
            customer_email: '',
            date: selectedDate,
            time: '19:00',
            duration_minutes: 90,
            party_size: 2,
            zone_preference: '',
            notes: '',
            special_requests: '',
            status: 'confirmee'
        });
    };
    
    // Navigation date
    const navigateDate = (direction) => {
        const date = new Date(selectedDate);
        date.setDate(date.getDate() + direction);
        setSelectedDate(date.toISOString().split('T')[0]);
    };
    
    // Filter reservations
    const filteredReservations = reservations.filter(r => {
        if (statusFilter !== 'all' && r.status !== statusFilter) return false;
        if (zoneFilter !== 'all' && r.table_zone !== zoneFilter) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (
                r.customer_name?.toLowerCase().includes(q) ||
                r.customer_phone?.includes(q) ||
                r.table_number?.toString().includes(q)
            );
        }
        return true;
    });
    
    // Stats
    const stats = todayData?.stats || {};
    const alerts = todayData?.alerts || [];
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }
    
    return (
        <div className="space-y-6 p-6" data-testid="reservations-page">
            {/* Mobile Page Header */}
            <MobilePageHeader 
                title="Réservations"
                subtitle={`${stats.total_reservations || 0} réservations`}
                icon={CalendarCheck}
                backPath="/dashboard"
            />
            
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Réservations & Plan de Salle</h1>
                    <p className="text-muted-foreground">Gestion unifiée des tables et réservations</p>
                    {/* Badge lecture seule pour serveurs */}
                    {isReadOnlyMode && (
                        <Badge className="mt-2 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            <Eye className="w-3 h-3 mr-1" />
                            Mode lecture seule
                        </Badge>
                    )}
                </div>
                
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}>
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-40"
                    />
                    <Button variant="outline" size="icon" onClick={() => navigateDate(1)}>
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={fetchData}
                        disabled={refreshing}
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                    {canManage && (
                        <Button onClick={() => { resetResForm(); setEditingReservation(null); setReservationDialog(true); }}>
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvelle réservation
                        </Button>
                    )}
                </div>
            </div>
            
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <KPICard 
                    title="Réservations" 
                    value={stats.total_reservations || 0}
                    subtitle={`${stats.reservations_confirmees || 0} confirmées`}
                    icon={ClipboardList}
                    color="text-blue-600"
                />
                <KPICard 
                    title="Couverts attendus" 
                    value={stats.total_covers_expected || 0}
                    subtitle={`${stats.total_covers_seated || 0} installés`}
                    icon={Users}
                    color="text-green-600"
                />
                <KPICard 
                    title="Tables dispo" 
                    value={stats.disponible || 0}
                    subtitle={`sur ${stats.total_tables || 0}`}
                    icon={CheckCircle2}
                    color="text-emerald-600"
                />
                <KPICard 
                    title="Occupées" 
                    value={stats.occupee || 0}
                    icon={Utensils}
                    color="text-orange-600"
                />
                <KPICard 
                    title="Réservées" 
                    value={stats.reservee || 0}
                    icon={Clock}
                    color="text-blue-600"
                />
                <KPICard 
                    title="No-show" 
                    value={stats.reservations_no_show || 0}
                    icon={Ban}
                    color="text-red-600"
                />
            </div>
            
            {/* Alerts */}
            {alerts.length > 0 && (
                <Card className="border-amber-200 bg-amber-50/50">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                            Alertes ({alerts.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                            {alerts.slice(0, 6).map((alert, i) => (
                                <AlertItem 
                                    key={i} 
                                    alert={alert} 
                                    onAction={(a) => {
                                        if (a.reservation_id) {
                                            const res = reservations.find(r => r.id === a.reservation_id);
                                            if (res) handleReservationAction('view', res);
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
            
            {/* Main Content Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                    <TabsTrigger value="floor" className="gap-2">
                        <MapPin className="w-4 h-4" />
                        Plan de salle
                    </TabsTrigger>
                    <TabsTrigger value="list" className="gap-2">
                        <ClipboardList className="w-4 h-4" />
                        Liste ({filteredReservations.length})
                    </TabsTrigger>
                </TabsList>
                
                {/* Floor Plan Tab */}
                <TabsContent value="floor" className="space-y-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-lg">Plan de salle interactif</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <FloorPlan
                                tables={tables}
                                selectedTable={selectedTable}
                                onSelectTable={setSelectedTable}
                                onTableAction={handleTableAction}
                                showLegend={true}
                                showZones={true}
                            />
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* List Tab */}
                <TabsContent value="list" className="space-y-4">
                    {/* Filters */}
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-wrap gap-4">
                                <div className="flex-1 min-w-[200px]">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Rechercher par nom, téléphone, table..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="pl-9"
                                        />
                                    </div>
                                </div>
                                
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-40">
                                        <SelectValue placeholder="Statut" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les statuts</SelectItem>
                                        {Object.entries(RESERVATION_STATUS).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                
                                <Select value={zoneFilter} onValueChange={setZoneFilter}>
                                    <SelectTrigger className="w-40">
                                        <SelectValue placeholder="Zone" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toutes les zones</SelectItem>
                                        {zones.map(z => (
                                            <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>
                    
                    {/* Reservations Grid */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {filteredReservations.length === 0 ? (
                            <Card className="col-span-full">
                                <CardContent className="p-8 text-center text-muted-foreground">
                                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Aucune réservation pour cette date</p>
                                    {canManage && (
                                        <Button 
                                            variant="outline" 
                                            className="mt-4"
                                            onClick={() => { resetResForm(); setReservationDialog(true); }}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Créer une réservation
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            filteredReservations.map(res => (
                                <ReservationCard
                                    key={res.id}
                                    reservation={res}
                                    onAction={handleReservationAction}
                                />
                            ))
                        )}
                    </div>
                </TabsContent>
            </Tabs>
            
            {/* Reservation Dialog */}
            <Dialog open={reservationDialog} onOpenChange={setReservationDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingReservation ? 'Modifier la réservation' : 'Nouvelle réservation'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingReservation 
                                ? `Réservation de ${editingReservation.customer_name}`
                                : 'Remplissez les informations de la réservation'
                            }
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid gap-4 py-4">
                        {/* Client Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2 sm:col-span-1">
                                <Label>Nom du client *</Label>
                                <Input
                                    value={resForm.customer_name}
                                    onChange={(e) => setResForm(prev => ({ ...prev, customer_name: e.target.value }))}
                                    placeholder="Nom complet"
                                />
                            </div>
                            <div className="col-span-2 sm:col-span-1">
                                <Label>Téléphone</Label>
                                <Input
                                    value={resForm.customer_phone}
                                    onChange={(e) => setResForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                                    placeholder="+241 XX XX XX XX"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={resForm.customer_email}
                                onChange={(e) => setResForm(prev => ({ ...prev, customer_email: e.target.value }))}
                                placeholder="email@example.com"
                            />
                        </div>
                        
                        {/* Date/Time */}
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label>Date *</Label>
                                <Input
                                    type="date"
                                    value={resForm.date}
                                    onChange={(e) => setResForm(prev => ({ ...prev, date: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label>Heure *</Label>
                                <Input
                                    type="time"
                                    value={resForm.time}
                                    onChange={(e) => setResForm(prev => ({ ...prev, time: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label>Durée (min)</Label>
                                <Select 
                                    value={resForm.duration_minutes.toString()} 
                                    onValueChange={(v) => setResForm(prev => ({ ...prev, duration_minutes: parseInt(v) }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="60">1h</SelectItem>
                                        <SelectItem value="90">1h30</SelectItem>
                                        <SelectItem value="120">2h</SelectItem>
                                        <SelectItem value="150">2h30</SelectItem>
                                        <SelectItem value="180">3h</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        {/* Party size & Zone */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Nombre de personnes *</Label>
                                <Select 
                                    value={resForm.party_size.toString()} 
                                    onValueChange={(v) => setResForm(prev => ({ ...prev, party_size: parseInt(v) }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[1,2,3,4,5,6,7,8,9,10,12,15,20].map(n => (
                                            <SelectItem key={n} value={n.toString()}>{n} personne{n > 1 ? 's' : ''}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Zone préférée</Label>
                                <Select 
                                    value={resForm.zone_preference || "none"} 
                                    onValueChange={(v) => setResForm(prev => ({ ...prev, zone_preference: v === "none" ? "" : v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Aucune préférence" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Aucune préférence</SelectItem>
                                        {zones.map(z => (
                                            <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        {/* Table Selection */}
                        <div>
                            <Label>Table</Label>
                            <Select 
                                value={resForm.table_id || "auto"} 
                                onValueChange={(v) => setResForm(prev => ({ ...prev, table_id: v === "auto" ? "" : v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Attribution automatique" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="auto">Attribution automatique</SelectItem>
                                    {availableTables.map(t => (
                                        <SelectItem key={t.id} value={t.id}>
                                            Table {t.number} ({t.zone}) - {t.capacity} places
                                            {t.capacity_match === 'exact' && <Badge className="ml-2 h-4">Idéale</Badge>}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {availableTables.length === 0 && resForm.date && resForm.time && (
                                <p className="text-xs text-amber-600 mt-1">
                                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                                    Aucune table disponible pour ce créneau
                                </p>
                            )}
                        </div>
                        
                        {/* Notes */}
                        <div>
                            <Label>Notes</Label>
                            <Textarea
                                value={resForm.notes}
                                onChange={(e) => setResForm(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Notes internes..."
                                rows={2}
                            />
                        </div>
                        
                        <div>
                            <Label>Demandes spéciales du client</Label>
                            <Textarea
                                value={resForm.special_requests}
                                onChange={(e) => setResForm(prev => ({ ...prev, special_requests: e.target.value }))}
                                placeholder="Anniversaire, allergies, chaise haute..."
                                rows={2}
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReservationDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSaveReservation}>
                            {editingReservation ? 'Enregistrer' : 'Créer la réservation'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Seat Customer Dialog */}
            <Dialog open={seatDialog} onOpenChange={setSeatDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Installer le client</DialogTitle>
                        <DialogDescription>
                            Confirmer l'installation et créer la commande
                        </DialogDescription>
                    </DialogHeader>
                    
                    {seatReservation && (
                        <div className="py-4 space-y-4">
                            <Alert>
                                <Users className="w-4 h-4" />
                                <AlertDescription>
                                    <div className="font-semibold">{seatReservation.customer_name}</div>
                                    <div className="text-sm">
                                        {seatReservation.party_size} personnes • Table {seatReservation.table_number}
                                    </div>
                                </AlertDescription>
                            </Alert>
                            
                            {seatReservation.special_requests && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <div className="text-sm font-medium text-amber-800 mb-1">Demandes spéciales:</div>
                                    <div className="text-sm text-amber-700">{seatReservation.special_requests}</div>
                                </div>
                            )}
                            
                            <p className="text-sm text-muted-foreground">
                                Une commande sera automatiquement créée pour cette table.
                            </p>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSeatDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSeatCustomer}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Installer & Créer commande
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ReservationsPage;
